import { useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import { ArrowUpRight, Loader2, Truck } from 'lucide-react';
import { fetchCategories, fetchProducts, API } from '@/lib/api';
import AddToQuoteButton from '@/components/quote/AddToQuoteButton';
import AddToCartButton from '@/components/cart/AddToCartButton';
import { formatAUD } from '@/context/CartContext';
import { CATALOG } from '@/constants/testIds';
import useReveal from '@/hooks/useReveal';
import { CATEGORY_FALLBACK_IMG } from '@/lib/imageFallbacks';
import { resolveImage as _resolveImage } from '@/lib/resolveImage';

const ALL = { slug: 'all', label: 'All' };
const LIST_CATEGORIES = ['poultry', 'smallgoods', 'sausages', 'burgers'];

const LIST_HEADINGS = {
  poultry: {
    eyebrow: 'Poultry',
    title: ['Free-range', 'chicken range'],
    blurb:
      'Free-range chicken cuts, Halal certified. Sold per kilo — cold-chain delivered across WA.',
  },
  smallgoods: {
    eyebrow: 'Smallgoods',
    title: ['House-cured', '& smoked'],
    blurb:
      'Made on-site in our Malaga cure room. Deli-sliced packets and whole cured meats.',
  },
  sausages: {
    eyebrow: 'Gluten-Free Sausages',
    title: ['Gluten-free,', 'house-made'],
    blurb:
      'Sixteen house-made varieties. Natural casing, gluten-free, ready to grill.',
  },
  burgers: {
    eyebrow: 'Burgers',
    title: ['Hand-pressed', 'patties & kofta'],
    blurb:
      'Hand-pressed daily. Wagyu, beef, lamb, chicken. Sold per kilo.',
  },
};

export default function Catalog() {
  const reveal = useReveal();
  const [active, setActive] = useState('all');

  const { data: categoriesData } = useSWR('categories', fetchCategories);
  const categories = [ALL, ...(categoriesData || [])];

  const { data: productsData, isLoading } = useSWR(
    ['products', active],
    ([, cat]) => fetchProducts(cat),
    { keepPreviousData: true }
  );
  const products = productsData || [];

  // Live next-slot preview — same source of truth as the cart
  const slotsFetcher = (path) => fetch(`${API}${path}`).then((r) => r.json());
  const { data: slots } = useSWR('/shop/slots', slotsFetcher);
  const nextSlotLabel = useMemo(() => {
    if (!slots?.modes) return null;
    const modes = ['pickup', 'metro', 'regional'];
    let earliest = null;
    let earliestMode = null;
    for (const m of modes) {
      const first = slots.modes[m]?.dates?.[0];
      if (first && (!earliest || first.date < earliest.date)) {
        earliest = first; earliestMode = m;
      }
    }
    if (!earliest) return null;
    const modeLabels = { pickup: 'Pickup', metro: 'Metro', regional: 'Regional' };
    return `${modeLabels[earliestMode]} · ${earliest.label}`;
  }, [slots]);

  // Memoize the "All" view splits to avoid re-running on every render.
  const gridProducts = useMemo(
    () => products.filter((p) => !LIST_CATEGORIES.includes(p.category)),
    [products]
  );
  const productsByListCategory = useMemo(() => {
    const map = {};
    for (const slug of LIST_CATEGORIES) map[slug] = [];
    for (const p of products) {
      if (LIST_CATEGORIES.includes(p.category)) map[p.category].push(p);
    }
    return map;
  }, [products]);

  // Allow other sections (Pillars, Wagyu) to set the filter
  useEffect(() => {
    const handler = (e) => {
      const slug = e?.detail;
      if (typeof slug === 'string') setActive(slug);
    };
    window.addEventListener('mw-catalog-filter', handler);
    return () => window.removeEventListener('mw-catalog-filter', handler);
  }, []);

  const activeCategory = categories.find((c) => c.slug === active);

  return (
    <section
      id="catalog"
      data-testid={CATALOG.section}
      className="relative py-24 sm:py-32 bg-[var(--mw-surface)] border-y border-white/10"
    >
      <div className="max-w-screen-2xl mx-auto px-6 sm:px-10 lg:px-16">
        <div ref={reveal} className="mw-reveal flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-12">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="block w-10 h-px bg-white/40" />
              <span className="text-[10px] uppercase tracking-[0.32em] text-white/55">
                Catalogue · 004
              </span>
            </div>
            <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl text-white leading-[0.95] tracking-tight max-w-2xl">
              Curated cuts for the
              <br />
              <em className="not-italic text-[var(--mw-primary)]">professional</em> kitchen.
            </h2>
          </div>
          <p className="text-white/60 max-w-md text-base leading-relaxed">
            {activeCategory?.tagline ||
              'Shop online with cold-chain delivery — or request a wholesale quote for bulk orders.'}
          </p>
        </div>

        <div className="flex flex-wrap gap-2 mb-12">
          {categories.map((c) => {
            const isActive = active === c.slug;
            return (
              <button
                key={c.slug}
                data-testid={CATALOG.filter(c.slug)}
                onClick={() => setActive(c.slug)}
                className={`text-[11px] uppercase tracking-[0.22em] px-4 py-2.5 border transition-colors ${
                  isActive
                    ? 'bg-[var(--mw-primary)] border-[var(--mw-primary)] text-white'
                    : 'border-white/15 text-white/70 hover:border-white hover:text-white'
                }`}
              >
                {c.label}
              </button>
            );
          })}
        </div>

        {isLoading && products.length === 0 ? (
          <div className="flex justify-center py-20" data-testid="catalog-loading">
            <Loader2 className="animate-spin text-white/40" />
          </div>
        ) : products.length === 0 ? (
          <div
            data-testid={CATALOG.empty}
            className="border border-white/10 py-20 text-center text-white/50 text-sm uppercase tracking-[0.24em]"
          >
            No products in this category
          </div>
        ) : LIST_CATEGORIES.includes(active) ? (
          <CategoryList products={products} category={active} nextSlotLabel={nextSlotLabel} />
        ) : active === 'all' ? (
          <>
            <div
              data-testid={CATALOG.grid}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8"
            >
              {gridProducts.map((p, i) => (
                <ProductCard key={p.id} product={p} index={i} nextSlotLabel={nextSlotLabel} />
              ))}
            </div>
            {LIST_CATEGORIES.map((slug) => (
              <CategoryList
                key={slug}
                products={productsByListCategory[slug]}
                category={slug}
                showHeading
                nextSlotLabel={nextSlotLabel}
              />
            ))}
          </>
        ) : (
          <div
            data-testid={CATALOG.grid}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8"
          >
            {products.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} nextSlotLabel={nextSlotLabel} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function CategoryList({ products, category, showHeading = false, nextSlotLabel }) {
  const reveal = useReveal();
  if (!products || products.length === 0) return null;
  const heading = LIST_HEADINGS[category];
  return (
    <div
      ref={reveal}
      data-testid={`catalog-${category}-list`}
      className={`mw-reveal border border-white/10 bg-[var(--mw-bg)] ${showHeading ? 'mt-16' : ''}`}
    >
      {showHeading && heading && (
        <div className="px-6 sm:px-10 pt-8 pb-6 border-b border-white/10 flex items-end justify-between gap-6 flex-wrap">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <span className="block w-8 h-px bg-white/40" />
              <span className="text-[10px] uppercase tracking-[0.32em] text-white/55">
                {heading.eyebrow}
              </span>
            </div>
            <h3 className="font-display font-light text-3xl sm:text-4xl text-white leading-[0.95] tracking-tight">
              {heading.title[0]} <em className="not-italic text-[var(--mw-primary)]">{heading.title[1]}</em>
            </h3>
            {nextSlotLabel && (
              <div
                data-testid={`catalog-${category}-next-slot`}
                className="mt-3 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-white/60 border border-white/10 bg-white/[0.02] px-2.5 py-1.5"
              >
                <Truck size={11} className="text-[var(--mw-primary)] shrink-0" />
                Earliest: {nextSlotLabel}
              </div>
            )}
          </div>
          <p className="text-white/55 max-w-sm text-sm leading-relaxed">
            {heading.blurb}
          </p>
        </div>
      )}
      <ul className="divide-y divide-white/10">
        {products.map((p) => (
          <li
            key={p.id}
            data-testid={CATALOG.card(p.id)}
            className="group grid grid-cols-12 gap-4 items-center px-6 sm:px-10 py-4 hover:bg-white/[0.03] transition-colors"
          >
            <div className="col-span-12 sm:col-span-4 flex items-center gap-4">
              <span className="text-[10px] uppercase tracking-[0.24em] text-white/35 tabular-nums w-6">
                {String(products.indexOf(p) + 1).padStart(2, '0')}
              </span>
              <h4 className="font-display text-lg sm:text-xl text-white leading-tight">
                {p.name}
              </h4>
            </div>
            <div className="col-span-8 sm:col-span-3 text-sm text-white/55 leading-relaxed">
              {p.description}
            </div>
            <div className="col-span-4 sm:col-span-2 text-right">
              <div className="font-display text-lg text-white tabular-nums leading-none">
                {formatAUD(p.price)}
              </div>
              <div className="text-[9px] uppercase tracking-[0.22em] text-white/40 mt-1">
                per {p.unit || 'kg'}
              </div>
            </div>
            <div className="col-span-12 sm:col-span-3 flex justify-end gap-2 flex-wrap">
              <AddToCartButton product={p} variant="compact" />
              <AddToQuoteButton product={p} variant="compact" />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ProductCard({ product, index, nextSlotLabel }) {
  const reveal = useReveal();
  return (
    <article
      ref={reveal}
      data-testid={CATALOG.card(product.id)}
      style={{ transitionDelay: `${(index % 4) * 80}ms` }}
      className="mw-reveal group bg-[var(--mw-bg)] border border-white/10 hover:border-[var(--mw-primary)]/70 transition-colors duration-300 flex flex-col"
    >
      <div className="relative aspect-[4/5] overflow-hidden">
        <img
          src={resolveImage(product)}
          alt={product.name}
          loading="lazy"
          decoding="async"
          width="480"
          height="600"
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.06]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
        <div className="absolute top-4 left-4 flex flex-col gap-1.5">
          <span className="text-[10px] uppercase tracking-[0.24em] bg-black/60 backdrop-blur-sm border border-white/15 text-white px-2 py-1">
            {product.grade}
          </span>
          {product.featured && (
            <span className="text-[10px] uppercase tracking-[0.24em] bg-[var(--mw-primary)] text-white px-2 py-1 inline-block">
              Featured
            </span>
          )}
        </div>
      </div>
      <div className="p-5 flex-1 flex flex-col">
        <div className="text-[10px] uppercase tracking-[0.28em] text-white/45">
          {product.cut}
        </div>
        <h3 className="font-display text-2xl text-white mt-2 leading-tight">
          {product.name}
        </h3>
        <p className="text-sm text-white/60 leading-relaxed mt-3 flex-1">
          {product.description}
        </p>
        <div className="mt-4 flex items-baseline justify-between">
          <div>
            <div className="font-display text-3xl text-white tabular-nums leading-none">
              {formatAUD(product.price)}
            </div>
            <div className="text-[10px] uppercase tracking-[0.24em] text-white/50 mt-1.5">
              per {product.unit || 'kg'}
            </div>
          </div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 text-right">
            {product.origin}
          </div>
        </div>
        {nextSlotLabel && (
          <div
            data-testid={`product-next-slot-${product.id}`}
            className="mt-3 flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-white/60 border border-white/10 bg-white/[0.02] px-2.5 py-1.5"
          >
            <Truck size={11} className="text-[var(--mw-primary)] shrink-0" />
            <span className="truncate">Earliest: {nextSlotLabel}</span>
          </div>
        )}
        <div className="mt-4 pt-4 border-t border-white/10 grid grid-cols-2 gap-2">
          <AddToCartButton product={product} />
          <AddToQuoteButton product={product} />
        </div>
      </div>
    </article>
  );
}


function resolveImage(product) {
  const src = product?.image_url;
  if (!src) return _resolveImage(CATEGORY_FALLBACK_IMG[product?.category] || CATEGORY_FALLBACK_IMG.beef, 480);
  return _resolveImage(src, 480);
}
